import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { stripeCheckoutLink } from '../../redux/actions/myAccountAction'

// These correspond to Stripe price.lookup_key values, resolved to actual price IDs on the backend
const PRO_TRIAL_PRICE_ID = 'pro_monthly'
const TRIAL_DAYS = 5

const GiftIcon: React.FC = () => (
  <svg
    className='trial-gift-svg'
    width='40'
    height='40'
    viewBox='0 0 48 48'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <defs>
      <linearGradient
        id='giftBorder'
        x1='0'
        y1='0'
        x2='48'
        y2='48'
        gradientUnits='userSpaceOnUse'
      >
        <stop offset='0' stopColor='#D8FFEF' />
        <stop offset='0.5' stopColor='#E4F0FF' />
        <stop offset='1' stopColor='#FCD2B3' />
      </linearGradient>
    </defs>
    {/* Gift box */}
    <rect
      x='9'
      y='16'
      width='30'
      height='22'
      rx='4'
      ry='4'
      fill='rgba(0, 0, 0, 0.25)'
      stroke='url(#giftBorder)'
      strokeWidth='2'
    />
    {/* Vertical ribbon */}
    <rect x='22' y='14' width='4' height='26' fill='url(#giftBorder)' />
    {/* Lid */}
    <rect
      x='7'
      y='14'
      width='34'
      height='6'
      rx='3'
      ry='3'
      fill='rgba(0, 0, 0, 0.5)'
      stroke='url(#giftBorder)'
      strokeWidth='1.6'
    />
    {/* Bow loops */}
    <path
      d='M24 6.5C22.2 4.5 19.6 3.5 17.7 4.4C15.8 5.3 15.4 7.4 16.5 9.1C17.6 10.7 19.7 11.7 21.7 11.9L24 12.1'
      fill='none'
      stroke='url(#giftBorder)'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
    <path
      d='M24 6.5C25.8 4.5 28.4 3.5 30.3 4.4C32.2 5.3 32.6 7.4 31.5 9.1C30.4 10.7 28.3 11.7 26.3 11.9L24 12.1'
      fill='none'
      stroke='url(#giftBorder)'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
    {/* Bow tails */}
    <path
      d='M24 12L21.5 14.5'
      stroke='url(#giftBorder)'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
    <path
      d='M24 12L26.5 14.5'
      stroke='url(#giftBorder)'
      strokeWidth='1.8'
      strokeLinecap='round'
    />
  </svg>
)

const TrialBanner: React.FC<{
  trialEligibility?: { isEligible: boolean; reason?: 'first_time' | 'reactivation' | 'ineligible' }
}> = ({ trialEligibility }) => {
  const { t } = useTranslation('offers')
  const [loading, setLoading] = useState(false)

  const selectedPriceId = PRO_TRIAL_PRICE_ID

  const giftLabel = trialEligibility?.reason === 'reactivation'
    ? t('trialBanner.giftWelcomeBack')
    : t('trialBanner.gift')

  const ctaLabel = t('trialBanner.cta', { days: String(TRIAL_DAYS) })

  const handleStartTrial = async () => {
    if (loading) return
    setLoading(true)
    try {
      const affiliateId = (window as any).affiliateId
      const payload: any = { priceId: selectedPriceId, trialDays: TRIAL_DAYS }
      if (affiliateId) {
        payload.affiliateId = affiliateId
      }

      // Trial tracking moved to PaymentSuccessful.tsx - fires after checkout completes

      const res: any = await stripeCheckoutLink(payload)
      if (res?.return_url) {
        window.open(res.return_url, '_self')
      } else if (res?.url) {
        window.open(res.url, '_self')
      }
    } catch (e) {
      // swallow error; user can still use other upgrade CTAs
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='upgrade_box_main_div trial-banner'>
      <div className='left_div'>
        <div className='trial-gift-icon'>
          <GiftIcon />
        </div>
        <div className='upgrade_contains'>
          <div className='trial-eyebrow'>{t('trialBanner.eyebrow')}</div>
          <h5>{t('trialBanner.title', { days: TRIAL_DAYS })}</h5>
          <div className='trial-gift-highlight'>{giftLabel}</div>
          <p className='trial-intro'>{t('trialBanner.intro')}</p>
          <p className='trial-intro'>{t('common:teacherLanguageNotice')}</p>
        </div>
      </div>
      <div className='right_div'>
        <Button
          type='button'
          className='common_btn mob_w_100 trial-cta-button'
          onClick={handleStartTrial}
          disabled={loading}
        >
          {loading ? (
            t('trialBanner.starting')
          ) : (
            <>
              <span className='trial-cta-label'>{ctaLabel}</span>
              <span className='trial-cta-arrow'>›</span>
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default TrialBanner
